// components/portrait-layers.js — FIVE-LAYER PORTRAIT TRANSPARENCY
// ═══════════════════════════════════════════════════════════
// Replaces raw fact view in Profile.
// Five layers: Who You Are / How You Operate /
//              What You're Building / What Gets In Your Way /
//              What Shift Is Still Learning
// Three actions per statement: This is right / Partially right / Wrong
// ═══════════════════════════════════════════════════════════
import { useState } from 'react';

const LAYERS = [
  {
    key: 'who_you_are',
    label: 'Who You Are',
    color: '#9b8fc4',
    categories: ['life', 'cultural', 'mission'],
    description: 'Your identity, values, and what drives you',
  },
  {
    key: 'how_you_operate',
    label: 'How You Operate',
    color: '#7a9e82',
    categories: ['patterns', 'preferences', 'skill_dna'],
    description: 'How you think, communicate, and work',
  },
  {
    key: 'what_youre_building',
    label: "What You're Building",
    color: '#c8a96e',
    categories: ['work', 'goals', 'professional_vertical'],
    description: 'Your projects, direction, and professional focus',
  },
  {
    key: 'what_gets_in_the_way',
    label: 'What Gets In Your Way',
    color: '#c46050',
    categories: ['_inferred_avoidance', '_inferred_decision'],
    description: 'Patterns Shift has noticed — blind spots, avoidance, contradictions',
  },
  {
    key: 'still_learning',
    label: 'What Shift Is Still Learning',
    color: '#888',
    categories: ['stakes', '_low_confidence'],
    description: 'Things Shift is less sure about — your input matters most here',
  },
];

function getFeedbackLabel(state) {
  if (state === 'right') return 'Confirmed';
  if (state === 'partial') return 'Partially right';
  if (state === 'wrong') return 'Corrected';
  return null;
}

export default function PortraitLayers({ portrait, syntheses, inferredPatterns, onFeedback }) {
  const [expandedLayer, setExpandedLayer] = useState(null);
  const [feedback, setFeedback] = useState({});

  function handleFeedback(key, value) {
    setFeedback(prev => ({ ...prev, [key]: value }));
    onFeedback?.(key, value);
  }

  // Build statements for each layer
  function getLayerStatements(layer) {
    const statements = [];

    if (layer.key === 'what_gets_in_the_way') {
      // Pull from inferred patterns
      if (inferredPatterns?.avoidance_pattern) {
        statements.push({
          key: 'avoidance',
          text: inferredPatterns.avoidance_pattern.description,
          confidence: inferredPatterns.avoidance_pattern.confidence,
          source: 'inferred',
        });
      }
      if (inferredPatterns?.decision_pattern) {
        statements.push({
          key: 'decision',
          text: inferredPatterns.decision_pattern.description,
          confidence: inferredPatterns.decision_pattern.confidence,
          source: 'inferred',
        });
      }
    } else if (layer.key === 'still_learning') {
      // Low confidence syntheses + stakes
      for (const cat of layer.categories) {
        if (cat === '_low_confidence') {
          // Find syntheses with low fact counts
          Object.entries(syntheses || {}).forEach(([catName, synth]) => {
            if (synth?.facts_count <= 2 && synth?.synthesis) {
              statements.push({
                key: `low_${catName}`,
                text: synth.synthesis,
                confidence: 0.3,
                source: 'synthesis',
                category: catName,
              });
            }
          });
        } else if (syntheses?.[cat]?.synthesis) {
          statements.push({
            key: cat,
            text: syntheses[cat].synthesis,
            confidence: syntheses[cat].facts_count > 3 ? 0.7 : 0.4,
            source: 'synthesis',
            category: cat,
          });
        }
      }
    } else {
      // Regular layers — pull from syntheses
      for (const cat of layer.categories) {
        if (syntheses?.[cat]?.synthesis) {
          statements.push({
            key: cat,
            text: syntheses[cat].synthesis,
            confidence: syntheses[cat].facts_count > 3 ? 0.8 : 0.5,
            source: 'synthesis',
            category: cat,
            confirmed: syntheses[cat].confirmed,
          });
        }
      }

      // Add strength pattern to "how you operate"
      if (layer.key === 'how_you_operate' && inferredPatterns?.strength_pattern) {
        statements.push({
          key: 'strength',
          text: inferredPatterns.strength_pattern.description,
          confidence: inferredPatterns.strength_pattern.confidence,
          source: 'inferred',
        });
      }
    }

    return statements;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {LAYERS.map(layer => {
        const statements = getLayerStatements(layer);
        const isExpanded = expandedLayer === layer.key;

        return (
          <div key={layer.key}>
            {/* Layer header */}
            <div
              onClick={() => setExpandedLayer(isExpanded ? null : layer.key)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px', cursor: 'pointer',
                background: isExpanded ? '#111' : 'transparent',
                border: `1px solid ${isExpanded ? layer.color + '33' : '#272736'}`,
                borderRadius: isExpanded ? '8px 8px 0 0' : 8,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.borderColor = layer.color + '44'; }}
              onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.borderColor = '#272736'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 4, height: 24, borderRadius: 2, background: layer.color, opacity: 0.6 }} />
                <div>
                  <div style={{ fontSize: 14, color: '#d4d4d4', fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
                    {layer.label}
                  </div>
                  <div style={{ fontSize: 11, color: '#555', fontFamily: 'Georgia, serif', marginTop: 2 }}>
                    {statements.length > 0 ? `${statements.length} insight${statements.length !== 1 ? 's' : ''}` : 'Building...'}
                  </div>
                </div>
              </div>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                <path d="M2 3.5l3 3 3-3" stroke={layer.color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {/* Layer content */}
            {isExpanded && (
              <div style={{
                border: `1px solid ${layer.color}33`, borderTop: 'none',
                borderRadius: '0 0 8px 8px', padding: '12px 16px',
                background: '#07070a',
              }}>
                <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic', fontFamily: 'Georgia, serif', marginBottom: 16 }}>
                  {layer.description}
                </div>

                {statements.length === 0 && (
                  <div style={{ color: '#333', fontSize: 13, fontStyle: 'italic', fontFamily: 'Georgia, serif', padding: '10px 0' }}>
                    Not enough data yet. Keep having conversations.
                  </div>
                )}

                {statements.map(stmt => {
                  const fb = feedback[stmt.key];
                  return (
                    <div key={stmt.key} style={{
                      padding: '12px 14px', marginBottom: 8,
                      background: '#111', border: '1px solid #272736', borderRadius: 6,
                    }}>
                      <div style={{ fontSize: 14, color: '#ccc', lineHeight: 1.7, fontFamily: 'Georgia, serif', marginBottom: 10 }}>
                        {stmt.text}
                      </div>

                      {/* Confidence bar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ flex: 1, height: 2, background: '#272736', borderRadius: 1 }}>
                          <div style={{ width: `${(stmt.confidence || 0.5) * 100}%`, height: 2, background: layer.color, borderRadius: 1, opacity: 0.5 }} />
                        </div>
                        <span style={{ fontSize: 9, color: '#444', fontFamily: 'Georgia, serif' }}>
                          {stmt.source === 'inferred' ? 'inferred' : 'observed'}
                        </span>
                      </div>

                      {/* Feedback buttons */}
                      {fb ? (
                        <div style={{ fontSize: 11, color: layer.color, fontFamily: 'Georgia, serif' }}>
                          {getFeedbackLabel(fb)}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                          {['right', 'partial', 'wrong'].map(val => (
                            <button key={val}
                              onClick={() => handleFeedback(stmt.key, val)}
                              style={{
                                background: 'none', border: '1px solid #2a2a2a', borderRadius: 4,
                                color: '#888', fontSize: 11, fontFamily: 'Georgia, serif',
                                padding: '4px 12px', cursor: 'pointer', transition: 'all 0.15s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = layer.color; e.currentTarget.style.color = layer.color; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
                            >
                              {val === 'right' ? 'This is right' : val === 'partial' ? 'Partially right' : 'Wrong'}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
